import React, { Component } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import breakpoints from "../../styles/breakpoints";
import { header } from "../../styles/variables";

import MainBottleInfo from "../../components/organisms/MainBottleInfo";
import WineListsSidePanel from "../../components/organisms/WineListsSidePanel";
import { checkScrollingDown } from "../../helpers/tools";
import {
  trackingEvents,
  trackingEventViewedWineDetailsOptions,
  useTrackEvent,
} from "../../helpers/Analytics";
import { LayoutContext } from "../../components/organisms/Layout/context";
import MyCellarReviewTray from "../../components/organisms/MyCellarReviewTray";

const WineDetailsContainer = styled.div`
  height: 100%;
  width: 100%;
  top: ${(props) => (props.showHeaderBarFromChild ? "80px" : "0px")};
  ${breakpoints.smUp} {
    padding-right: 80px;
  }
  ${breakpoints.smDown} {
    padding-right: 0px;
  }
`;

const infoBoxFullPageStyleWithHeader = css`
  ${breakpoints.smUp} {
    top: ${header.mobileHeight};
  }
  ${breakpoints.mdUp} {
    top: ${header.desktopHeight};
  }
  top: 0px;
`;

const infoBoxFullPageStyleWithoutHeader = css`
  top: 0px !important;
`;

/**
 * Component for rendering Wine's details.
 */
class BottleInfo extends Component {
  static contextType = LayoutContext;

  static propTypes = {
    wine: PropTypes.shape({}).isRequired,
    memberShoppingCartQuery: PropTypes.shape({}).isRequired,
    memberId: PropTypes.number,
    history: PropTypes.shape({}).isRequired,
    trackEvent: PropTypes.func.isRequired,
  };

  static defaultProps = {
    memberId: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      isWineListsPanelOpen: false,
      isAddBottlePanelOpen: false,
      isReviewTrayOpen: false,
      showHeaderBarFromChild: true,
    };
    this.previousScrollY = 0;
  }

  componentDidMount() {
    const { wine, trackEvent } = this.props;
    const { showHeaderBarFromChild } = this.state;
    const { setShowHeaderBarFromChild } = this.context;
    setShowHeaderBarFromChild(showHeaderBarFromChild);
    trackEvent(trackingEvents.TRACKING_EVENT_VIEWED_WINE_DETAILS, {
      [trackingEventViewedWineDetailsOptions.WINE_SLUG]: wine.product.slug,
      [trackingEventViewedWineDetailsOptions.WINE_ID]: wine.id,
    });
    return window.addEventListener("scroll", this.handleScroll);
  }

  componentDidUpdate(prevProps, prevState) {
    const { showHeaderBarFromChild } = this.state;
    const { showHeaderBarFromChild: previousShowHeaderBarFromChild } = prevState;
    if (showHeaderBarFromChild !== previousShowHeaderBarFromChild) {
      const { setShowHeaderBarFromChild } = this.context;
      setShowHeaderBarFromChild(showHeaderBarFromChild);
    }
  }

  componentWillUnmount() {
    return window.removeEventListener("scroll", this.handleScroll);
  }

  /**
   * Handler for scroll event listeners,
   * ...to set state to show or hide Header bar.
   * */
  handleScroll = () => {
    const { isScrollingDown, yValue } = checkScrollingDown(this.previousScrollY);
    this.previousScrollY = yValue;
    const { showHeaderBarFromChild } = this.state;
    if (showHeaderBarFromChild === isScrollingDown) {
      // CASE: If scrolling down, hides header if shown
      // and, if scrolling up, shows header if hidden.
      this.setState({ showHeaderBarFromChild: !isScrollingDown });
    }
  };

  /**
   * Toggles open/close of the wine lists panel.
   */
  handleOffCanvasChange = () => {
    this.setState((prevState) => ({
      isWineListsPanelOpen: !prevState.isWineListsPanelOpen,
      showHeaderBarFromChild: true,
    }));
  };

  /**
   * Toggles open/close of the "Add bottle" panel.
   */
  handleAddBottlePanelChange = () => {
    this.setState((prevState) => ({
      isAddBottlePanelOpen: !prevState.isAddBottlePanelOpen,
      showHeaderBarFromChild: true,
    }));
  };

  /**
   * Toggles open/close of the "Add bottle" panel.
   */
  handleReviewTrayOpenChange = () => {
    this.setState((prevState) => ({
      isReviewTrayOpen: !prevState.isReviewTrayOpen,
    }));
  };

  /**
   * This function is called inside <Header> Component,
   * when header is shown/hidden on scroll.
   *
   * @param {Boolean} isShown
   * */
  setHeaderDisplay = (isShown = true) => {
    this.setState({ showHeaderBarFromChild: isShown });
  };

  render() {
    const { isWineListsPanelOpen, showHeaderBarFromChild, isReviewTrayOpen } = this.state;
    const { wine, memberId, memberShoppingCartQuery, history } = this.props;

    return (
      <>
        <WineDetailsContainer showHeaderBarFromChild={showHeaderBarFromChild}>
          <MainBottleInfo
            wine={wine}
            handleOffCanvasChange={this.handleOffCanvasChange}
            handleAddBottlePanelChange={this.handleAddBottlePanelChange}
            memberShoppingCartQuery={memberShoppingCartQuery}
            infoBoxItemProductHolderStyle={
              showHeaderBarFromChild ? infoBoxFullPageStyleWithHeader : infoBoxFullPageStyleWithoutHeader
            }
            setHeaderDisplay={() => this.setHeaderDisplay()}
            onReviewWineClick={() => this.handleReviewTrayOpenChange()}
            isWineDetailsPage
            memberId={memberId}
          />
        </WineDetailsContainer>
        {isWineListsPanelOpen && (
          <WineListsSidePanel
            handleOffCanvasChange={this.handleOffCanvasChange}
            memberId={memberId}
            wine={wine}
            history={history}
          />
        )}
        {isReviewTrayOpen && (
          <MyCellarReviewTray
            selectedWine={wine}
            onCloseButtonClick={() => this.handleReviewTrayOpenChange()}
            isOpened={isReviewTrayOpen}
            // TODO: Find solution for adding setRandomVariableRewards
            setRandomVariableRewards={() => {}}
          />
        )}
      </>
    );
  }
}

const BottleInfoWrapper = (props) => {
  const trackEvent = useTrackEvent();

  return <BottleInfo {...props} trackEvent={trackEvent} />;
};

export default BottleInfoWrapper;
